import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { assertWritable } from '../../services/canvasSchema';
import type { SchemaInfo } from '../../services/canvasSchema';
import { normalizeCanvas } from '../../utils/canvasSnapshot';
import { Node, Edge } from '@xyflow/react';
import i18n from '../../i18n';
import * as repo from '../../services/repo';   // 모드(local|qbase)에 따라 로컬 저장소 또는 QBase
import { be1Fetch } from '../../config/api';
import { errorFromResponse } from '../../services/errorText';

export interface Project {
  id: string;
  name: string;
  description?: string;
  finalWorkflowId?: string;
  /** 지금 사용자의 역할 — 저장 API(프로젝트 권한)를 쓸 때만. 없으면 예전처럼 모두 편집 가능 */
  myRole?: 'admin' | 'owner' | 'editor' | 'viewer' | null;
  /** 나와 이 프로젝트의 관계 — owner(내가 만든 것) · editor/viewer(공유받은 것). 저장 API 에만 있다(예전 경로는 없음) */
  relation?: 'owner' | 'editor' | 'viewer' | null;
  /** 소유자 로그인 ID (예전 행은 usrid) */
  ownerUsrid?: string;
}

export interface Workflow {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  icon?: string;
  /** 프로젝트 아래 폴더 경로 — 'a/b' 처럼 '/' 로 잇는다. 비거나 없으면 프로젝트 바로 아래(루트) */
  folder?: string;
  /** 서버에 저장된 최신 캔버스 버전 (0 = 아직 저장 없음) */
  curVerNo?: number;
  /** 처음 만든 때·마지막으로 바꾼 때 (ISO) — 대시보드 「최근 작성·변경」 순서 */
  createdAt?: string;
  updatedAt?: string;
  /** 캔버스 캐시 — undefined 면 아직 서버에서 읽지 않은 것, [] 는 읽었는데 비어 있는 것 */
  nodes?: Node[];
  edges?: Edge[];
  settings?: any;
  /** 캐시된 캔버스를 읽을 때의 형식 상태 — 다시 열어도 같은 안내·차단이 유지되도록 함께 들고 다닌다 */
  schemaInfo?: SchemaInfo | null;
  /**
   * 캐시(nodes·edges)가 **몇 버전에서** 읽혔거나 저장됐는지. curVerNo 가 이보다 크면 그 사이 다른 곳(속성 일괄수정,
   * 다른 사용자)이 새 버전을 저장한 것이다 — 열 때 새로고침을 묻는다(openWorkflow).
   */
  baseVerNo?: number;
  /**
   * 사용자가 캔버스를 **일부러 비웠다**(노드·연결선을 전부 지움, 아직 저장 안 함).
   * 빈 캐시는 보통 믿지 않는다(읽기 실패로 남은 빈 캔버스일 수 있어 서버에서 다시 읽는다 — openWorkflow).
   * 이 표시가 있으면 편집이므로 그대로 둔다. 예전에는 구분하지 못해, 다 지우고 다른 워크플로에 갔다 오면
   * 마지막에 지운 것이 되살아났다(2026-10-12).
   */
  userEmptied?: boolean;
}

export interface ProjectState {
  projects: Project[];
  selectedProjectId: string | null;
  workflows: Workflow[];
  loading: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  error: string | null;
  /**
   * 워크플로 목록 읽기의 상태 — **실패와 '정말 0건' 을 가르려고** 따로 둔다.
   * 공용 `loading`/`error` 는 프로젝트 읽기·저장과 뒤섞여서, 목록이 비어 보이는 이유를 말해 주지 못했다.
   */
  workflowsStatus: 'idle' | 'loading' | 'ready' | 'failed';
  workflowsError: string | null;
  /** 워크플로별 '저장된 캔버스' 정규화 스냅샷 (불러오기/저장 시점). 현재 캔버스와 다르면 미저장 변경(dirty) */
  savedSnapshots: Record<string, string>;
  /** 설치 단위 기본 시간대 (서버 OPENFLOW_TZ) — utils/timeZone */
  systemTimeZone: string;
  /** 프로젝트별 설정 — 지금은 시간대 재정의(없으면 null = 시스템 기본) */
  projectSettings: Record<string, { timeZone: string | null }>;
}

// 영속화는 QBase (VW_OPENFLOW_PROJECT / _WORKFLOW / _WORKFLOW_VER) — 하드코딩 초기값 없음.
// 로그인 후 loadProjects → 프로젝트 선택 시 loadWorkflows → 캔버스 열 때 loadWorkflowCanvas.
const initialState: ProjectState = {
  systemTimeZone: 'Asia/Seoul',
  projectSettings: {},
  projects: [],
  selectedProjectId: null,
  workflows: [],
  loading: false,
  saving: false,
  lastSavedAt: null,
  error: null,
  workflowsStatus: 'idle',
  workflowsError: null,
  savedSnapshots: {},
};

// ---------------------------------------------------------------- thunks (서버 왕복)

export const loadProjects = createAsyncThunk('project/loadProjects', () => repo.listProjects());

/** 설치 단위 기본 시간대 — 앱이 뜰 때 한 번 */
export const loadSystemTimeZone = createAsyncThunk('project/loadSystemTimeZone', async () => {
  const r = await be1Fetch('/api/system/time-zone', undefined, { retries: 0 });
  if (!r.ok) throw await errorFromResponse(r, r.statusText);
  return String((await r.json()).timeZone || 'Asia/Seoul');
});

/** 프로젝트 설정(시간대) — 프로젝트를 고를 때 */
export const loadProjectSettings = createAsyncThunk('project/loadProjectSettings', async (projectId: string) => {
  const r = await be1Fetch(`/api/projects/${encodeURIComponent(projectId)}/settings`, undefined, { retries: 0 });
  if (!r.ok) throw await errorFromResponse(r, r.statusText);
  const body = await r.json();
  return { projectId, timeZone: (body.timeZone as string | null) ?? null };
});

/** 프로젝트 시간대를 정하거나(null/'' → 시스템 기본을 따름) 바꾼다 */
export const saveProjectSettings = createAsyncThunk(
  'project/saveProjectSettings',
  async ({ projectId, timeZone }: { projectId: string; timeZone: string | null }) => {
    const r = await be1Fetch(`/api/projects/${encodeURIComponent(projectId)}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ timeZone: timeZone || null }),
    }, { retries: 0 });
    if (!r.ok) throw await errorFromResponse(r, r.statusText);
    const body = await r.json();
    return { projectId, timeZone: (body.timeZone as string | null) ?? null };
  },
);

export const loadWorkflows = createAsyncThunk('project/loadWorkflows', (projectId: string) => repo.listWorkflows(projectId));

export const createProject = createAsyncThunk(
  'project/createProject',
  (arg: { name: string; description?: string; ownerUsrid: string }) =>
    repo.createProject({ name: arg.name, description: arg.description }, arg.ownerUsrid),
);

export const createWorkflow = createAsyncThunk(
  'project/createWorkflow',
  (arg: { projectId: string; name: string; description?: string; icon?: string; folder?: string }) => repo.createWorkflow(arg),
);

export const saveWorkflowMeta = createAsyncThunk(
  'project/saveWorkflowMeta',
  async (arg: { id: string; name: string; description?: string; folder?: string }) => { await repo.updateWorkflowMeta(arg); return arg; },
);

export const retireWorkflow = createAsyncThunk(
  'project/retireWorkflow',
  async (workflowId: string) => { await repo.retireWorkflow(workflowId); return workflowId; },
);

/** 캔버스 로드 — 서버의 CUR_VER_NO 버전을 읽어 캐시에 넣는다. 저장이 없던 워크플로는 빈 캔버스. */
export const loadWorkflowCanvas = createAsyncThunk(
  'project/loadWorkflowCanvas',
  async (workflowId: string, { getState }) => {
    const wf = (getState() as { project: ProjectState }).project.workflows.find(w => w.id === workflowId);
    if (!wf) throw new Error(i18n.t('common:error.workflowNotFound', { id: workflowId }));
    const doc = await repo.loadCanvas(workflowId, wf.curVerNo ?? 0);
    // 저장된 버전이 있다고 했는데 그 내용을 못 읽었다 — 빈 캔버스로 넘기면 화면이 "노드가 없다" 고
    // 말해 버리고, 그 상태로 저장하면 내용이 사라진다. 읽기 실패는 실패로 알린다.
    if (!doc && (wf.curVerNo ?? 0) >= 1) {
      throw new Error(i18n.t('common:error.canvasVersionMissing', { ver: wf.curVerNo }));
    }
    return {
      id: workflowId, nodes: doc?.nodes ?? [], edges: doc?.edges ?? [], settings: doc?.settings,
      schemaInfo: doc?.migration ?? null,
    };
  },
);

/** 캔버스 저장 — 새 버전 행 추가 + 헤더 CUR_VER_NO 갱신 */
export const saveWorkflowCanvas = createAsyncThunk(
  'project/saveWorkflowCanvas',
  async (arg: { id: string; nodes: Node[]; edges: Edge[]; settings?: any; regUsrid: string }, { getState }) => {
    // 형식이 더 새로우면 저장하지 않는다. 화면(헤더 버튼)뿐 아니라 AI 도우미 도구·마법사 등
    // 다른 호출자도 여기를 지나므로, 한 곳에서 막아야 빠짐이 없다.
    assertWritable(
      (getState() as { project: ProjectState }).project.workflows.find(w => w.id === arg.id)?.schemaInfo,
      'save',
    );
    const wf = (getState() as { project: ProjectState }).project.workflows.find(w => w.id === arg.id);
    if (!wf) throw new Error(i18n.t('common:error.workflowNotFound', { id: arg.id }));
    const ver = await repo.saveCanvas(arg.id, wf.curVerNo ?? 0, { nodes: arg.nodes, edges: arg.edges, settings: arg.settings }, arg.regUsrid);
    return { id: arg.id, verNo: ver, nodes: arg.nodes, edges: arg.edges, settings: arg.settings };
  },
);

// ---------------------------------------------------------------- slice

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setSelectedProject: (state, action: PayloadAction<string | null>) => {
      state.selectedProjectId = action.payload;
      // 고른 순간부터 '불러오는 중' 이다. 여기서 안 바꾸면 앞 프로젝트의 결과(성공이든 실패든)가
      // 목록 요청이 날아가기 전까지 그대로 보인다.
      state.workflowsStatus = action.payload ? 'loading' : 'idle';
      state.workflowsError = null;
    },
    /** 캔버스를 화면에 다 흘려 넣은 뒤(리듀서가 필드를 보태므로) 실제 스토어 상태로 '저장된 스냅샷'을 맞춘다 */
    setSavedSnapshot: (state, action: PayloadAction<{ id: string; nodes: Node[]; edges: Edge[]; settings?: any }>) => {
      state.savedSnapshots[action.payload.id] = normalizeCanvas(action.payload.nodes, action.payload.edges, action.payload.settings);
    },
    /** 캔버스 편집 중 메모리 캐시 갱신 (서버 저장은 saveWorkflowCanvas) */
    updateWorkflowData: (state, action: PayloadAction<{ id: string; nodes: Node[]; edges: Edge[]; settings?: any; userEmptied?: boolean }>) => {
      const { id, nodes, edges, settings } = action.payload;
      const index = state.workflows.findIndex(workflow => workflow.id === id);
      if (index !== -1) {
        const empty = nodes.length === 0 && edges.length === 0;
        state.workflows[index] = { ...state.workflows[index], nodes, edges, ...(settings !== undefined ? { settings } : {}),
          userEmptied: empty && !!action.payload.userEmptied };
      }
    },
    setFinalWorkflowId: (state, action: PayloadAction<{ projectId: string; workflowId: string }>) => {
      const { projectId, workflowId } = action.payload;
      const project = state.projects.find(p => p.id === projectId);
      if (project) {
        project.finalWorkflowId = workflowId;
      }
    },
    clearProjectError: (state) => { state.error = null; },
    resetProjectState: () => initialState,
  },
  extraReducers: (b) => {
    const fail = (state: ProjectState, action: any) => {
      state.loading = false; state.saving = false;
      state.error = action.error?.message || i18n.t('common:error.requestFailed');
    };

    b.addCase(loadProjects.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(loadProjects.fulfilled, (s, a) => { s.loading = false; s.projects = a.payload; });
    b.addCase(loadProjects.rejected, fail);

    b.addCase(loadWorkflows.pending, (s) => { s.loading = true; s.error = null; s.workflowsStatus = 'loading'; s.workflowsError = null; });
    b.addCase(loadSystemTimeZone.fulfilled, (s, a) => { s.systemTimeZone = a.payload; });
    b.addCase(loadProjectSettings.fulfilled, (s, a) => { s.projectSettings[a.payload.projectId] = { timeZone: a.payload.timeZone }; });
    b.addCase(saveProjectSettings.fulfilled, (s, a) => { s.projectSettings[a.payload.projectId] = { timeZone: a.payload.timeZone }; });
    b.addCase(loadWorkflows.fulfilled, (s, a) => {
      s.loading = false;
      s.workflowsStatus = 'ready';
      s.workflowsError = null;
      // 다른 프로젝트의 워크플로는 유지, 이 프로젝트 것만 서버 결과로 교체 (캔버스 캐시는 보존)
      const projectId = a.meta.arg;
      const cache = new Map(s.workflows.filter(w => w.projectId === projectId).map(w => [w.id, w]));
      // 버전이 바뀌어도 캐시를 버리지 않는다 — 저장하지 않은 편집일 수 있다. 몇 버전에서 읽은 것인지(baseVerNo)를
      // 같이 들고 있다가, 열 때 새 버전이 있으면 묻는다(openWorkflow). 예전에는 말없이 버려 편집이 사라질 수 있었다.
      const fresh = a.payload.map(w => {
        const old = cache.get(w.id);
        if (!old || old.nodes === undefined) return w;
        return { ...w, nodes: old.nodes, edges: old.edges, settings: old.settings, schemaInfo: old.schemaInfo,
          baseVerNo: old.baseVerNo ?? old.curVerNo ?? 0, userEmptied: old.userEmptied };
      });
      s.workflows = [...s.workflows.filter(w => w.projectId !== projectId), ...fresh];
    });
    b.addCase(loadWorkflows.rejected, (s, a) => {
      fail(s, a);
      // 실패를 '0건' 과 갈라 둔다 — 화면이 "워크플로 없음" 이라고 말해 버리면 사용자는 사라진 줄 안다
      s.workflowsStatus = 'failed';
      s.workflowsError = a.error?.message || i18n.t('common:error.workflowListFailed');
    });

    b.addCase(createProject.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(createProject.fulfilled, (s, a) => { s.saving = false; s.projects.push(a.payload); });
    b.addCase(createProject.rejected, fail);

    b.addCase(createWorkflow.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(createWorkflow.fulfilled, (s, a) => { s.saving = false; s.workflows.push({ updatedAt: new Date().toISOString(), ...a.payload, nodes: [], edges: [], baseVerNo: 0 }); });
    b.addCase(createWorkflow.rejected, fail);

    b.addCase(saveWorkflowMeta.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(saveWorkflowMeta.fulfilled, (s, a) => {
      s.saving = false;
      const w = s.workflows.find(x => x.id === a.payload.id);
      if (w) {
        w.name = a.payload.name; w.description = a.payload.description;
        if (a.payload.folder !== undefined) w.folder = a.payload.folder;
        w.updatedAt = new Date().toISOString();
      }
    });
    b.addCase(saveWorkflowMeta.rejected, fail);

    b.addCase(retireWorkflow.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(retireWorkflow.fulfilled, (s, a) => { s.saving = false; s.workflows = s.workflows.filter(w => w.id !== a.payload); });
    b.addCase(retireWorkflow.rejected, fail);

    b.addCase(loadWorkflowCanvas.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(loadWorkflowCanvas.fulfilled, (s, a) => {
      s.loading = false;
      const w = s.workflows.find(x => x.id === a.payload.id);
      if (w) { w.nodes = a.payload.nodes; w.edges = a.payload.edges; w.settings = a.payload.settings; w.schemaInfo = a.payload.schemaInfo; w.baseVerNo = w.curVerNo ?? 0; }
      s.savedSnapshots[a.payload.id] = normalizeCanvas(a.payload.nodes, a.payload.edges, a.payload.settings);
    });
    b.addCase(loadWorkflowCanvas.rejected, fail);

    b.addCase(saveWorkflowCanvas.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(saveWorkflowCanvas.fulfilled, (s, a) => {
      s.saving = false; s.lastSavedAt = Date.now();
      const w = s.workflows.find(x => x.id === a.payload.id);
      if (w) { w.curVerNo = a.payload.verNo; w.baseVerNo = a.payload.verNo; w.nodes = a.payload.nodes; w.edges = a.payload.edges; w.settings = a.payload.settings; w.updatedAt = new Date().toISOString(); w.userEmptied = false; }
      s.savedSnapshots[a.payload.id] = normalizeCanvas(a.payload.nodes, a.payload.edges, a.payload.settings);
    });
    b.addCase(saveWorkflowCanvas.rejected, fail);
  },
});

export const {
  setSelectedProject,
  updateWorkflowData,
  setFinalWorkflowId,
  clearProjectError,
  resetProjectState,
  setSavedSnapshot,
} = projectSlice.actions;
export default projectSlice.reducer;
